import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './components/AuthContext';
import { MobileMenuProvider } from './components/MobileMenuContext';
import { NotificationsProvider } from './context/NotificationsContext';
import { Toaster } from 'react-hot-toast'; 
import ErrorBoundary from './components/ErrorBoundary';

// Assurez-vous que les chemins d'accès sont corrects !
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import Dashboard from './pages/Dashboard';
import BulletinDePaie from './pages/BulletinDePaie';
import EmployeeCreation from './pages/EmployeeCreation';
import Documents from './pages/Documents';
import Employees from './pages/Employees';
import Fiche from './pages/Fiche';
import EmployeeEdit from './pages/EmployeeEdit';
import Avenant from './pages/Avenant';
import ArchivesPage from './pages/ArchivesPage';
import DocumentViewer from './pages/DocumentViewer';
import FinContrat from './pages/FinContrat';
import ContratsExpires from './pages/ContratsExpires';
import Domiciliation from './pages/Domiciliation';
import FidexIA from './pages/FidexIA';
import SendEmail from './pages/SendEmail';
import FidexEmail from './pages/FidexEmail';

// Composant pour protéger les routes (inchangé)
const ProtectedRoute: React.FC<{ element: React.ReactElement }> = ({ element }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <span className="loading loading-spinner loading-lg text-warning"></span>
      </div>
    );
  }
  return user ? element : <Navigate to="/login" replace />;
};

function App() {
  return (
    // 1. BrowserRouter doit être au niveau racine
    <Router>
      <AuthProvider>
        <MobileMenuProvider>
        {/* Monte une seule fois les hooks de notifications et d'alertes, que la
            barre de navigation et le panneau partagent. */}
        <NotificationsProvider>
          <Toaster
          position="top-right"
          containerStyle={{
            zIndex: 9999,
          }}
          gutter={12}
          toastOptions={{
            duration: 3000,
            style: {
              background: '#363636',
              color: '#fff',
              fontSize: '14px',
              padding: '16px',
              borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            },
            success: {
              duration: 3000,
              iconTheme: {
                primary: '#10b981',
                secondary: '#fff',
              },
            },
            error: {
              duration: 4000,
              iconTheme: {
                primary: '#ef4444',
                secondary: '#fff',
              },
            },
          }}
        />
        <ErrorBoundary>
        <Routes>
          {/* Route accessible sans connexion (inchangée) */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />

          {/* Route Protégée : DASHBOARD (inchangée) */}
          <Route 
            path="/dashboard" 
            element={<ProtectedRoute element={<Dashboard />} />} 
          />
          <Route 
            path="/bulletin-de-paie" 
            element={<ProtectedRoute element={<BulletinDePaie />} />} 
          />

          {/* AJOUT DE LA ROUTE PROTÉGÉE POUR LA CRÉATION D'EMPLOYÉS */}
          <Route 
            path="/creation-employes" 
            element={<ProtectedRoute element={<EmployeeCreation />} />} 
          />


          {/* Route pour la gestion des documents */}
          <Route 
            path="/documents" 
            element={<ProtectedRoute element={<Documents />} />} 
          />

          {/* Routes pour les employés */}
          <Route 
            path="/employees" 
            element={<ProtectedRoute element={<Employees />} />} 
          />
          
          <Route 
            path="/employees/:id" 
            element={<ProtectedRoute element={<Fiche />} />} 
          />

          <Route 
            path="/employees/:id/edit" 
            element={<ProtectedRoute element={<EmployeeEdit />} />} 
          />

          <Route 
            path="/employees/:id/avenant" 
            element={<ProtectedRoute element={<Avenant />} />} 
          />

          <Route 
            path="/employees/:id/archives" 
            element={<ProtectedRoute element={<ArchivesPage />} />} 
          />

          <Route 
            path="/employees/:id/documents/:documentKey" 
            element={<ProtectedRoute element={<Documents />} />} 
          />

          {/* Route pour visualiser les documents */}
          <Route 
            path="/contracts/templates/:id" 
            element={<ProtectedRoute element={<DocumentViewer />} />} 
          />
          {/* Tolérance au slash final et sous-segments éventuels */}
          <Route 
            path="/contracts/templates/:id/*" 
            element={<ProtectedRoute element={<DocumentViewer />} />} 
          />

          {/* Route pour les fins de contrat */}
          <Route
            path="/fin-contrat"
            element={<ProtectedRoute element={<FinContrat />} />}
          />
          <Route
            path="/contrats-expires"
            element={<ProtectedRoute element={<ContratsExpires />} />}
          />
          
          {/* Route pour la domiciliation de salaire */}
          <Route
            path="/domiciliation"
            element={<ProtectedRoute element={<Domiciliation />} />}
          />

          {/* Route pour Fidex IA */}
          <Route
            path="/fidex-ia"
            element={<ProtectedRoute element={<FidexIA />} />}
          />

          {/* Routes pour les emails */}
          <Route
            path="/fidex-email"
            element={<ProtectedRoute element={<FidexEmail />} />}
          />
          {/* Anciennes routes maintenues pour compatibilité */}
          <Route
            path="/send-email"
            element={<ProtectedRoute element={<SendEmail />} />}
          />

          {/* Redirection par défaut vers /dashboard (inchangée) */}
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
        </Routes>
        </ErrorBoundary>
        </NotificationsProvider>
        </MobileMenuProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;